import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { PaginatedApiResponse } from 'domain/Common';
import { axiosBaseQuery, getApiBaseUrl } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';
import { getEventConfig } from 'natalie/eventConfigs';

import {
  mapAccountlessEventFromApi,
  mapNatalieEventDetailFromApi,
  mapNataliePublicEventDetailFromApi,
} from './mappers';

import type {
  AccountlessNatalieEventApiResponse,
  CreateNatalieEventApiParams,
  GetAccountlessLiveKitTokenParams,
  GetLiveKitTokenData,
  GetLiveKitTokenParams,
  GetNatalieEventListApiParams,
  NatalieEventDetail,
  NatalieEventListItem,
  NatalieEventPublicDetail,
  RegisterToNatalieEventArgs,
  ScheduleAccountlessReminderArgs,
  UpdateNatalieEventApiParams,
} from './natalieEvent.types';

// natalie-api base queries (new standalone service)
const natalieApiBaseQuery = axiosBaseQuery({ baseURL: () => configService.getUrl(UrlNames.NatalieApi) });
const natalieApiBaseQueryWithRetry = retry(natalieApiBaseQuery, { maxRetries: 3 });

// Backend-api base query (kept for requestAgentToLeave, not migrated to natalie-api)
const backendApiBaseQuery = axiosBaseQuery({ baseURL: getApiBaseUrl('v1') });

// Accountless natalie-api (separate service, no auth required for unauthenticated users)
const accountlessBaseQueryWithRetry = retry(
  axiosBaseQuery({ baseURL: () => configService.getUrl(UrlNames.AccountlessNatalieApi) }),
  { maxRetries: 3 }
);

export const natalieEventApi = createApi({
  reducerPath: 'natalieEventApi',
  tagTypes: ['NATALIE_EVENT_LIST', 'NATALIE_EVENT_DETAILS'],
  baseQuery: natalieApiBaseQuery,
  endpoints: (builder) => ({
    getNatalieEventList: builder.query<PaginatedApiResponse<NatalieEventListItem>, GetNatalieEventListApiParams>({
      providesTags: ['NATALIE_EVENT_LIST'],
      query: ({ page, size, searchText, communityId, statuses }) => ({
        url: '/natalie',
        params: { page, size, searchText, communityId, statuses },
      }),
    }),
    createNatalieEvent: builder.mutation<NatalieEventListItem, CreateNatalieEventApiParams>({
      query: (data) => ({
        url: '/natalie',
        method: 'POST',
        data: { ...data, communityId: String(data.communityId) },
      }),
      invalidatesTags: ['NATALIE_EVENT_LIST'],
    }),
    updateNatalieEvent: builder.mutation<NatalieEventListItem, UpdateNatalieEventApiParams>({
      query: ({ id, ...data }) => ({
        url: `/natalie/${id}`,
        method: 'PUT',
        data: { ...data, communityId: String(data.communityId) },
      }),
      invalidatesTags: (_result, _error, { id }) => ['NATALIE_EVENT_LIST', { type: 'NATALIE_EVENT_DETAILS', id }],
    }),
    getPublicNatalieEvent: builder.query<NatalieEventPublicDetail, string>({
      providesTags: (_result, _error, id) => [{ type: 'NATALIE_EVENT_DETAILS', id }],
      query: (id) => ({
        url: `/natalie/${id}`,
      }),
      transformResponse: mapNataliePublicEventDetailFromApi,
    }),
    getNatalieEvent: builder.query<NatalieEventDetail, string>({
      providesTags: (_result, _error, id) => [{ type: 'NATALIE_EVENT_DETAILS', id }],
      query: (id) => ({
        url: `/natalie/${id}`,
      }),
      transformResponse: mapNatalieEventDetailFromApi,
    }),
    registerToNatalieEvent: builder.mutation<void, RegisterToNatalieEventArgs>({
      query: ({ id }) => ({
        url: `/natalie/${id}/register`,
        method: 'POST',
      }),
    }),
    // requestAgentToLeave — remains on backend-api (not migrated to natalie-api)
    requestAgentToLeave: builder.mutation<void, string>({
      queryFn: async (id, api, extraOptions) => {
        const result = await backendApiBaseQuery({ url: `/natalie/${id}/leave`, method: 'POST' }, api, extraOptions);
        if (result.error) return { error: result.error };
        return { data: result.data as void };
      },
    }),
    getLiveKitToken: builder.mutation<GetLiveKitTokenData, GetLiveKitTokenParams>({
      queryFn: async ({ eventId, version, slot, mode, agentName, debug, testInviteCode }, api, extraOptions) => {
        // The token endpoint uses version + slot params to configure RoomAgentDispatch
        const params: Record<string, string> = {};
        if (version) params.version = version;
        if (slot) params.slot = slot;
        if (mode) params.mode = mode;

        // Read agent config from sessionStorage (set by Configure page)
        const sessionAgentConfig = sessionStorage.getItem('natalie-agent-config');
        let agentConfig: string | null = sessionAgentConfig;

        // Fall back to per-event config when no manual config exists
        if (!agentConfig) {
          const eventConfig = getEventConfig(eventId);
          if (eventConfig) {
            agentConfig = JSON.stringify(eventConfig);
          }
        }

        // Inject agentName into agentConfig (backend extracts it from metadata)
        if (agentName) {
          const configObj = agentConfig ? JSON.parse(agentConfig) : {};
          configObj.agentName = agentName;
          agentConfig = JSON.stringify(configObj);
        }

        // Inject debug flag into agentConfig
        if (debug) {
          const configObj = agentConfig ? JSON.parse(agentConfig) : {};
          configObj.debug = true;
          agentConfig = JSON.stringify(configObj);
        }

        const result = await natalieApiBaseQueryWithRetry(
          {
            url: '/natalie/token',
            method: 'POST',
            // testInviteCode lets the server resolve source=test and return isTest
            // for the Registered test Room (the real Registered Room omits it).
            data: {
              eventId,
              ...(agentConfig ? { agentConfig } : {}),
              ...(testInviteCode ? { testInviteCode } : {}),
            },
            params: Object.keys(params).length > 0 ? params : undefined,
          },
          api,
          extraOptions
        );

        // Clear sessionStorage after use (only if it was the source)
        if (sessionAgentConfig) sessionStorage.removeItem('natalie-agent-config');

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as GetLiveKitTokenData };
      },
    }),
    // Accountless: fetch event details from natalie-api (no auth)
    getAccountlessNatalieEvent: builder.query<NatalieEventPublicDetail, string>({
      queryFn: async (eventId, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry({ url: `/natalie/${eventId}` }, api, extraOptions);
        if (result.error) return { error: result.error };
        return { data: mapAccountlessEventFromApi(result.data as AccountlessNatalieEventApiResponse) };
      },
    }),
    // Accountless: generate LiveKit token from natalie-api (no auth)
    getAccountlessLiveKitToken: builder.mutation<GetLiveKitTokenData, GetAccountlessLiveKitTokenParams>({
      queryFn: async ({ eventId, inviteCode }, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry(
          {
            url: `/natalie/${eventId}/token/${inviteCode}`,
            method: 'POST',
          },
          api,
          extraOptions
        );
        if (result.error) return { error: result.error };
        return { data: result.data as GetLiveKitTokenData };
      },
    }),
    // Accountless: schedule a reminder email (no auth)
    scheduleAccountlessReminder: builder.mutation<void, ScheduleAccountlessReminderArgs>({
      queryFn: async ({ eventId, inviteCode, ...body }, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry(
          {
            url: `/natalie/${eventId}/${inviteCode}/reminder`,
            method: 'POST',
            data: body,
          },
          api,
          extraOptions
        );
        if (result.error) return { error: result.error };
        return { data: undefined };
      },
    }),
  }),
});

export const {
  useGetNatalieEventListQuery,
  useCreateNatalieEventMutation,
  useUpdateNatalieEventMutation,
  useGetPublicNatalieEventQuery,
  useGetNatalieEventQuery,
  useGetLiveKitTokenMutation,
  useRequestAgentToLeaveMutation,
  useRegisterToNatalieEventMutation,
  useGetAccountlessNatalieEventQuery,
  useGetAccountlessLiveKitTokenMutation,
  useScheduleAccountlessReminderMutation,
} = natalieEventApi;
